import type {
  ConversationListParams,
  ModerationListParams,
  PageParams,
  PersonaDocumentsParams,
  UUID,
} from './types';

/**
 * Centralized, hierarchical query keys. Invalidate broadly (e.g. `products.all`)
 * or narrowly (`products.detail(id)`). Keeps cache invalidation predictable.
 */
export const queryKeys = {
  me: ['me'] as const,

  examBoards: {
    all: ['exam-boards'] as const,
    // O perfil entra na chave porque cada perfil tem um conjunto PRÓPRIO de
    // bancas — a lista de Concursos não é a de Vestibular refiltrada.
    list: (profile?: string) => ['exam-boards', 'list', profile ?? ''] as const,
  },

  whatsappLink: ['whatsapp-link'] as const,
  whatsappGroups: ['whatsapp-groups'] as const,
  // Fora do prefixo dos grupos: os candidatos mudam com o elenco do dono, não
  // com o registro ou a remoção de um grupo.
  whatsappGroupAgents: ['whatsapp-group-agents'] as const,

  documents: {
    all: ['documents'] as const,
    // `params` carries the area filter too, so switching areas is a different
    // query — never the same cache entry refetched under a new filter.
    list: (params?: PageParams) => ['documents', 'list', params ?? {}] as const,
  },

  areas: {
    all: ['knowledge-areas'] as const,
    // The profile rides in the key: an admin narrowing the vocabulary to one
    // profile is a DIFFERENT vocabulary, never the same one refetched. So is
    // another user's personal shelf (`userId`), the admin-only read behind the
    // inventory's area editor.
    list: (options?: { profile?: string; userId?: UUID }) =>
      ['knowledge-areas', 'list', options?.profile ?? '', options?.userId ?? ''] as const,
  },

  profiles: {
    all: ['profiles'] as const,
    // Sob o prefixo dos perfis: toda escrita num perfil muda o que a exclusão
    // de algum deles moveria, e cai junto com a lista.
    deletionImpact: (slug: string) => ['profiles', 'deletion-impact', slug] as const,
  },

  myDependents: {
    // Sem filtro nenhum: a lista é a do responsável e vem inteira, em qualquer
    // status — por isso `all` serve de chave e de prefixo ao mesmo tempo.
    all: ['my-dependents'] as const,
    // Os lembretes são de UM dependente e nunca de todos: a chave carrega o id
    // para que criar um lembrete de um não invalide a lista aberta de outro.
    reminders: (id: string) => ['my-dependents', 'reminders', id] as const,
  },

  taxonomy: {
    all: ['taxonomy'] as const,
    list: (profile?: string) => ['taxonomy', 'list', profile ?? ''] as const,
  },

  ingestionRuns: {
    all: ['ingestion-runs'] as const,
    list: (params?: PageParams) => ['ingestion-runs', 'list', params ?? {}] as const,
  },

  decks: {
    all: ['decks'] as const,
    list: (params?: PageParams) => ['decks', 'list', params ?? {}] as const,
  },

  trainings: {
    all: ['trainings'] as const,
    list: (params?: PageParams) => ['trainings', 'list', params ?? {}] as const,
  },

  interactions: {
    all: ['interactions'] as const,
    list: (params?: PageParams) => ['interactions', 'list', params ?? {}] as const,
    detail: (id: string) => ['interactions', 'detail', id] as const,
  },

  conversations: {
    all: ['conversations'] as const,
    // The search text rides in the key: a search is a different listing, never
    // the recent one refetched under a filter.
    list: (params?: ConversationListParams) => ['conversations', 'list', params ?? {}] as const,
    detail: (id: string) => ['conversations', 'detail', id] as const,
  },

  metrics: {
    overview: ['metrics', 'overview'] as const,
    cost: ['metrics', 'cost'] as const,
  },

  dashboard: {
    summary: ['dashboard', 'summary'] as const,
  },

  users: {
    all: ['users'] as const,
    list: (params?: PageParams) => ['users', 'list', params ?? {}] as const,
  },

  aiModelSettings: ['ai-model-settings'] as const,

  marketingPopup: {
    public: ['public-marketing-popup'] as const,
    admin: ['admin-marketing-popup'] as const,
  },
  siteContent: {
    public: ['public-site-content'] as const,
    admin: ['admin-site-content'] as const,
  },
  socialLibrary: ['social-library'] as const,

  agents: {
    all: ['agents'] as const,
    // The roster is partitioned by profile, so a narrowed listing is a DIFFERENT
    // roster — never the whole one refetched under a filter.
    list: (profile?: string) => ['agents', 'list', profile ?? ''] as const,
    // Omitting `params` yields the PREFIX of every page of one persona's
    // candidates, which is what a link write has to invalidate: the filters the
    // picker happened to be on are not the write's business.
    documents: (id: string, params?: PersonaDocumentsParams) =>
      ['agents', 'documents', id, ...(params ? [params] : [])] as const,
  },

  myPersonas: {
    // Sem filtro nenhum: a lista é a do leitor e vem inteira, em qualquer
    // status — é por isso que `all` serve de chave e de prefixo ao mesmo tempo.
    all: ['my-personas'] as const,
    documents: (id: string, params?: PersonaDocumentsParams) =>
      ['my-personas', 'documents', id, ...(params ? [params] : [])] as const,
  },

  moderation: {
    all: ['moderation'] as const,
    list: (params?: ModerationListParams) => ['moderation', 'list', params ?? {}] as const,
    detail: (id: string) => ['moderation', 'detail', id] as const,
  },

  // Billing: chaves planas como whatsappLink — cada leitura é sempre a MESMA do
  // chamador (ou o catálogo único), sem filtro que a chave precisasse carregar.
  billingPlans: ['billing-plans'] as const,
  billingSubscription: ['billing-subscription'] as const,
  billingPayments: ['billing-payments'] as const,
  billingReferrals: ['billing-referrals'] as const,
} as const;
